"use client";

import React, { useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { FilterX, Loader, Search } from "lucide-react";
import { toast } from "sonner";

import { useModalStore } from "@/hooks/useModal";
import { SearchButton } from "@/components/molecules/SearchButton";
import PlaceSearchInput from "@/components/molecules/PlaceSearch";
import { PriceRangeField } from "@/components/molecules/PropertySelect/PriceRangeField";
import { RoomsDropdown } from "@/components/molecules/PropertySelect/RoomsField";
import { PropertyTypeField } from "@/components/molecules/PropertySelect/PropertyField";
import { usePropertySearchStore } from "@/stores/usePropertySearchStore";
import { usePropertySearch } from "@/hooks/usePropertySearch";
import type { Category } from "@/app/types";
import { LocationData } from "@/app/stores/places";

interface CompactedSearchFormProps {
  showRooms?: boolean;
  showSearch?: boolean;
}

export const CompactedSearchForm = ({
  showRooms = true,
  showSearch = true,
  translations
}: CompactedSearchFormProps & { translations: Record<string, any> }) => {
  const router = useRouter();
  const pathname = usePathname();
  const { openModal } = useModalStore();
  const { filters, setFilter } = usePropertySearchStore();
  const { executeSearch } = usePropertySearch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const success = await executeSearch();
      if (!success) {
        toast.error("Please provide valid search criteria");
        return;
      }

      if (pathname === "/properties") {
        router.push("/properties/list");
      }
    } catch {
      toast.error("Search failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDropdownValue = (value: number | Category | null) =>
    value === null
      ? { id: "", name: translations.all }
      : typeof value === "number"
        ? { id: String(value), name: String(value) }
        : value;

  const priceRangeValue = {
    id: "",
    name:
      filters.minPrice || filters.maxPrice
        ? `${filters.minPrice ? `$${filters.minPrice.toLocaleString()}` : translations.anyPrice} - ${filters.maxPrice ? `$${filters.maxPrice.toLocaleString()}` : translations.anyPrice
        }`
        : translations.anyPrice,
    min: filters.minPrice ?? undefined,
    max: filters.maxPrice ?? undefined,
  };

  return (
    <section className="relative z-40 w-full">
      <div className="mx-auto max-w-screen-2xl">
        <form
          aria-label="Property search form"
          className="lg:shadow-1 relative flex flex-col justify-between gap-4 rounded-lg bg-white px-5 py-11 dark:bg-black lg:flex-row lg:gap-x-5 lg:backdrop-blur"
          onSubmit={handleSearch}
        >
          <PropertyTypeField
            value={formatDropdownValue(filters.category)}
            onChange={(value) => setFilter("category", value?.id ? value : null)}
            label={translations.propertyType}
          />

          <PriceRangeField
            value={priceRangeValue}
            onChange={(range) => {
              setFilter("minPrice", range?.min ?? null);
              setFilter("maxPrice", range?.max ?? null);
            }}
            label={translations.priceRange}
          />

          <PlaceSearchInput
            borderless
            value={filters.location?.address ?? ""}
            onChange={(location) => {
              setFilter("location", location ? (location as LocationData) : null);
            }}
            placeholder={translations.location}
          />

          {showRooms && (
            <>
              <RoomsDropdown
                value={formatDropdownValue(filters.bedrooms)}
                onChange={(value) =>
                  setFilter("bedrooms", value?.id ? parseInt(value.id) : null)}
                label={translations.bedrooms}
              />
              <RoomsDropdown
                value={formatDropdownValue(filters.bathrooms)}
                onChange={(value) =>
                  setFilter("bathrooms", value?.id ? parseInt(value.id) : null)}
                label={translations.bathrooms}
              />
            </>
          )}

          <SearchButton
            type="button"
            icon={<FilterX size={25} />}
            label={translations.advanced}
            className="property-search-secondary"
            aria-label="Reset filters"
            onClick={() => openModal("property")}
          />

          {showSearch && (
            <SearchButton
              type="submit"
              icon={
                isSubmitting ? (
                  <Loader size={25} className="animate-spin" />
                ) : (
                  <Search size={25} />
                )
              }
              label={isSubmitting ? translations.searching : translations.search}
              className="property-search-primary"
              aria-label="Search properties"
              disabled={isSubmitting}
            />
          )}
        </form>
      </div>
    </section>
  );
};
